import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Loader2,
  LogOut,
  RefreshCw,
  CreditCard,
  Landmark,
  Trash2,
  Users,
  Sunrise,
  Sunset,
  Printer,
  CheckCircle2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "apa_admin_token";
const DAY_LABELS = { 14: "Mon 14", 15: "Tue 15", 16: "Wed 16", 17: "Thu 17" };
const REGISTER_DAYS = [
  { id: "14", label: "Monday 14 Dec" },
  { id: "15", label: "Tuesday 15 Dec" },
  { id: "16", label: "Wednesday 16 Dec" },
  { id: "17", label: "Thursday 17 Dec" },
];
const CHILDCARE_LABELS = {
  tax_free_childcare: "Tax-Free Childcare",
  edenred: "Edenred Childcare Vouchers",
  pluxee: "Pluxee (Sodexo)",
  care4: "Care-4 Childcare Vouchers",
};

const statusStyle = (s) => {
  if (s === "completed") return "bg-green-100 text-green-700";
  if (s === "awaiting_childcare_payment") return "bg-yellow-100 text-yellow-700";
  if (["failed", "expired"].includes(s)) return "bg-red-100 text-red-700";
  return "bg-slate-100 text-slate-600";
};

export default function AdminBookings() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState(null);
  const [view, setView] = useState("bookings");
  const [analytics, setAnalytics] = useState(null);

  const load = useCallback(async (tok) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/bookings`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setBookings(data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setError("Your session expired. Please log in again.");
      } else {
        setError("Could not load bookings.");
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  useEffect(() => {
    if (view !== "analytics" || !token) return;
    let cancelled = false;
    const fetchAnalytics = async () => {
      try {
        const res = await axios.get(`${API}/admin/analytics`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setAnalytics(res.data);
      } catch {
        /* keep last known numbers */
      }
    };
    fetchAnalytics();
    const t = setInterval(fetchAnalytics, 30000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [view, token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      if (!err?.response) {
        setError(
          "Cannot reach the server right now — the site is still connecting to its domain. Please try again shortly."
        );
      } else if (err.response.status === 429) {
        setError("Too many attempts. Please wait 15 minutes and try again.");
      } else {
        setError(
          typeof err?.response?.data?.detail === "string"
            ? err.response.data.detail
            : "Incorrect email or password"
        );
      }
    }
    setLoading(false);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setBookings(null);
  };

  const deleteBooking = async (b) => {
    if (
      !window.confirm(
        `Delete the booking for ${b.parent_name} (ref ${b.booking_ref})? This cannot be undone.`
      )
    )
      return;
    try {
      await axios.delete(`${API}/admin/bookings/${b.booking_ref}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBookings((prev) =>
        (prev || []).filter((x) => x.booking_ref !== b.booking_ref)
      );
    } catch (e) {
      setError("Could not delete that booking. Please try again.");
    }
  };

  const markPaid = async (b) => {
    if (
      !window.confirm(
        `Confirm you've received ${b.parent_name}'s payment? They'll be emailed a payment confirmation straight away.`
      )
    )
      return;
    try {
      await axios.post(
        `${API}/admin/bookings/${b.booking_ref}/mark-paid`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBookings((prev) =>
        (prev || []).map((x) =>
          x.booking_ref === b.booking_ref
            ? { ...x, status: "completed", payment_status: "paid" }
            : x
        )
      );
    } catch (e) {
      setError("Could not update that booking. Please try again.");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-900 text-white flex flex-col items-center justify-center px-5">
        <Link
          to="/"
          className="mb-8 inline-flex items-center gap-2 font-display font-extrabold text-lg"
        >
          <img
            src="/images/logo.png"
            alt="Action Packed Adventures logo"
            className="h-9 w-9 rounded-full object-cover"
          />
          Action Packed <span className="text-sky-400">Adventures</span>
        </Link>
        <form
          onSubmit={handleLogin}
          data-testid="admin-login-form"
          className="w-full max-w-sm rounded-[2rem] bg-white/5 border border-white/10 p-8 backdrop-blur-xl"
        >
          <h1 className="font-display font-black text-2xl mb-1">Admin login</h1>
          <p className="text-slate-400 text-sm mb-6 font-medium">
            View and manage camp bookings.
          </p>
          <label className="block text-sm font-bold text-slate-300 mb-1.5">
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="admin-email-input"
            className="w-full rounded-2xl border-2 border-white/10 bg-slate-800 px-4 py-3 font-semibold outline-none focus:border-sky-400 mb-4"
            placeholder="you@email.com"
          />
          <label className="block text-sm font-bold text-slate-300 mb-1.5">
            Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
            className="w-full rounded-2xl border-2 border-white/10 bg-slate-800 px-4 py-3 font-semibold outline-none focus:border-sky-400 mb-4"
            placeholder="••••••••"
          />
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-red-300 mb-4">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-button"
            className="w-full rounded-full bg-sky-400 py-3.5 font-display font-bold text-slate-900 hover:bg-sky-300 transition-colors disabled:opacity-70 inline-flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Log in"}
          </button>
        </form>
      </div>
    );
  }

  const totalRevenue = (bookings || [])
    .filter((b) => b.status === "completed")
    .reduce((s, b) => s + (b.amount || 0), 0);

  const register = REGISTER_DAYS.map((d) => ({
    ...d,
    entries: (bookings || []).flatMap((b) =>
      (b.children || [])
        .filter((c) => (c.days || []).map(String).includes(d.id))
        .map((c) => ({
          ...c,
          parent_name: b.parent_name,
          status: b.status,
        }))
    ),
  }));

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 text-white print:hidden">
        <div className="mx-auto max-w-7xl px-5 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-display font-extrabold">
            <img
              src="/images/logo.png"
              alt="Action Packed Adventures logo"
              className="h-9 w-9 rounded-full object-cover"
            />
            Bookings Dashboard
          </Link>
          <div className="flex items-center gap-3">
            <button
              onClick={() => load(token)}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-bold hover:bg-white/20 transition-colors"
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 rounded-full bg-sky-400 text-slate-900 px-4 py-2 text-sm font-bold hover:bg-sky-300 transition-colors"
            >
              <LogOut className="h-4 w-4" /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 print:hidden">
        <div
          className="mb-6 inline-flex rounded-full bg-white border-2 border-slate-900/10 p-1 shadow-sm"
          data-testid="admin-view-toggle"
        >
          {[
            { id: "bookings", label: "All bookings" },
            { id: "byDay", label: "By day" },
            { id: "analytics", label: "Visitors" },
          ].map((v) => (
            <button
              key={v.id}
              onClick={() => setView(v.id)}
              data-testid={`view-${v.id === "byDay" ? "by-day" : v.id}`}
              className={`rounded-full px-5 py-2 text-sm font-bold transition-colors ${
                view === v.id
                  ? "bg-slate-900 text-white"
                  : "text-slate-500 hover:text-slate-900"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <StatCard label="Total bookings" value={bookings ? bookings.length : "—"} />
          <StatCard
            label="Paid"
            value={
              bookings
                ? bookings.filter((b) => b.status === "completed").length
                : "—"
            }
          />
          <StatCard
            label="Received"
            value={`£${(totalRevenue / 100).toFixed(0)}`}
          />
        </div>

        {view === "analytics" ? (
          <div className="space-y-4" data-testid="analytics-section">
            {!analytics ? (
              <div className="flex justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-sky-400" />
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="rounded-2xl bg-slate-900 p-5 shadow-sm">
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-400 flex items-center gap-2">
                      <span className="relative flex h-2.5 w-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                      </span>
                      On the site now
                    </p>
                    <p
                      className="mt-2 font-display font-black text-4xl text-white"
                      data-testid="analytics-live"
                    >
                      {analytics.live_now}
                    </p>
                  </div>
                  <div
                    className="rounded-2xl bg-white border-2 border-slate-900/5 p-5 shadow-sm"
                    data-testid="analytics-today"
                  >
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
                      Visitors today
                    </p>
                    <p className="mt-2 font-display font-black text-4xl text-slate-900">
                      {analytics.today}
                    </p>
                  </div>
                  <div
                    className="rounded-2xl bg-white border-2 border-slate-900/5 p-5 shadow-sm"
                    data-testid="analytics-total"
                  >
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
                      Total visitors
                    </p>
                    <p className="mt-2 font-display font-black text-4xl text-slate-900">
                      {analytics.total_sessions}
                    </p>
                  </div>
                  <div
                    className="rounded-2xl bg-white border-2 border-slate-900/5 p-5 shadow-sm"
                    data-testid="analytics-pageviews"
                  >
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-400">
                      Page views
                    </p>
                    <p className="mt-2 font-display font-black text-4xl text-slate-900">
                      {analytics.total_pageviews}
                    </p>
                  </div>
                </div>

                <div className="rounded-2xl bg-white border-2 border-slate-900/5 p-5 shadow-sm">
                  <p className="font-display font-bold text-slate-900 mb-4">
                    Visitors, last 7 days
                  </p>
                  <div className="flex items-end gap-2 h-36">
                    {analytics.days.map((d, i) => {
                      const max = Math.max(
                        1,
                        ...analytics.days.map((x) => x.count)
                      );
                      return (
                        <div
                          key={i}
                          className="flex-1 flex flex-col items-center justify-end gap-1 h-full"
                          data-testid={`analytics-day-${i}`}
                        >
                          <span className="text-xs font-bold text-slate-700">
                            {d.count > 0 ? d.count : ""}
                          </span>
                          <div
                            className="w-full rounded-t-lg bg-sky-400"
                            style={{
                              height: `${(d.count / max) * 100}%`,
                              minHeight: d.count > 0 ? "8px" : "2px",
                              backgroundColor:
                                d.count > 0 ? undefined : "#e2e8f0",
                            }}
                          />
                          <span className="text-[10px] font-semibold text-slate-400 text-center">
                            {d.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <p className="text-xs text-slate-400 font-medium">
                  Privacy-friendly counting — no cookies and no personal data.
                  Your own visits to the admin area aren&apos;t counted.
                </p>
              </>
            )}
          </div>
        ) : view === "byDay" ? (
          <div>
            <div className="mb-4 flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-500">
                {register.reduce((n, d) => n + d.entries.length, 0)} child
                bookings across the week
              </p>
              <button
                onClick={() => window.print()}
                data-testid="print-register-button"
                className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white px-4 py-2 text-sm font-bold hover:bg-sky-500 transition-colors"
              >
                <Printer className="h-4 w-4" /> Print register
              </button>
            </div>
            <div
              className="grid gap-4 md:grid-cols-2 xl:grid-cols-4"
              data-testid="register-view"
            >
            {register.map((d) => (
              <div
                key={d.id}
                data-testid={`register-day-${d.id}`}
                className="rounded-2xl bg-white border-2 border-slate-900/5 p-5 shadow-sm"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display font-extrabold text-slate-900">
                    {d.label}
                  </h3>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-100 px-3 py-1 text-xs font-bold text-sky-700">
                    <Users className="h-3.5 w-3.5" />
                    {d.entries.length}
                  </span>
                </div>
                {d.entries.length === 0 ? (
                  <p className="text-sm text-slate-400 font-medium">
                    Nobody booked yet.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {d.entries.map((e, ei) => (
                      <div
                        key={ei}
                        data-testid={`register-entry-${d.id}-${ei}`}
                        className="rounded-xl bg-slate-50 border border-slate-100 p-3 text-sm"
                      >
                        <p className="font-bold text-slate-900">
                          {e.name}
                          {e.school_year ? (
                            <span className="text-slate-400 font-semibold">
                              {" "}
                              · {e.school_year}
                            </span>
                          ) : null}
                        </p>
                        <p className="text-slate-500 font-medium">
                          Parent: {e.parent_name}
                          {e.status !== "completed" && (
                            <span className="text-yellow-600">
                              {" "}
                              · {(e.status || "").replace(/_/g, " ")}
                            </span>
                          )}
                        </p>
                        {(e.early_dropoff || e.late_collection) && (
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {e.early_dropoff && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-bold text-yellow-700">
                                <Sunrise className="h-3 w-3" /> 8.00am drop-off
                              </span>
                            )}
                            {e.late_collection && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-bold text-indigo-700">
                                <Sunset className="h-3 w-3" /> 5.30pm collection
                              </span>
                            )}
                          </div>
                        )}
                        {e.medical_info &&
                          e.medical_info.trim().toLowerCase() !== "none" && (
                            <p className="mt-1 text-red-600 font-semibold">
                              Medical: {e.medical_info}
                            </p>
                          )}
                        <p className="mt-1 text-slate-600">
                          <span className="font-bold">Emergency:</span>{" "}
                          {[
                            e.emergency_contact_name,
                            e.emergency_contact_phone,
                          ]
                            .filter(Boolean)
                            .join(" · ") || "—"}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            </div>
          </div>
        ) : loading && !bookings ? (
          <div className="py-20 text-center text-slate-400">
            <Loader2 className="h-8 w-8 mx-auto animate-spin" />
          </div>
        ) : bookings && bookings.length === 0 ? (
          <p className="py-20 text-center text-slate-400 font-medium">
            No bookings yet.
          </p>
        ) : (
          <div className="space-y-4" data-testid="bookings-list">
            {(bookings || []).map((b, i) => (
              <div
                key={b.booking_ref || i}
                data-testid={`booking-row-${i}`}
                className="rounded-2xl bg-white border-2 border-slate-900/5 p-5 md:p-6 shadow-sm"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="font-display font-extrabold text-slate-900">
                      {b.parent_name}
                    </span>
                    <span className="text-sm text-slate-400">{b.parent_email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600">
                      {b.payment_method === "childcare" ? (
                        <Landmark className="h-3.5 w-3.5" />
                      ) : (
                        <CreditCard className="h-3.5 w-3.5" />
                      )}
                      {b.payment_method === "childcare"
                        ? CHILDCARE_LABELS[b.childcare_scheme] || "Childcare"
                        : "Card"}
                    </span>
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-bold ${statusStyle(
                        b.status
                      )}`}
                    >
                      {(b.status || "").replace(/_/g, " ")}
                    </span>
                    <span className="font-display font-black text-sky-600">
                      £{((b.amount || 0) / 100).toFixed(0)}
                    </span>
                  </div>
                </div>

                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {(b.children || []).map((c, ci) => (
                    <div
                      key={ci}
                      className="rounded-xl bg-slate-50 border border-slate-100 p-3 text-sm"
                    >
                      <p className="font-bold text-slate-900">
                        {c.name}
                        {c.school_year ? (
                          <span className="text-slate-400 font-semibold">
                            {" "}
                            · {c.school_year}
                          </span>
                        ) : null}
                      </p>
                      <p className="text-slate-500 font-medium">
                        {c.days?.length === 4
                          ? "Full 4-day camp"
                          : (c.days || [])
                              .map((d) => DAY_LABELS[d] || d)
                              .join(", ")}
                      </p>
                      {c.medical_info && (
                        <p className="mt-1 text-slate-600">
                          <span className="font-bold">Medical:</span>{" "}
                          {c.medical_info}
                        </p>
                      )}
                      {(c.emergency_contact_name || c.emergency_contact_phone) && (
                        <p className="mt-1 text-slate-600">
                          <span className="font-bold">Emergency:</span>{" "}
                          {[
                            c.emergency_contact_name,
                            c.emergency_contact_phone,
                            c.emergency_contact_email,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-slate-400 font-semibold">
                  <span>Ref: {b.booking_ref || "—"}</span>
                  <span>
                    {b.created_at ? new Date(b.created_at).toLocaleString("en-GB") : ""}
                  </span>
                  <div className="ml-auto flex items-center gap-2">
                    {b.payment_method === "childcare" &&
                      b.status === "awaiting_childcare_payment" && (
                        <button
                          onClick={() => markPaid(b)}
                          data-testid={`mark-paid-${i}`}
                          className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1.5 text-xs font-bold text-emerald-600 hover:bg-emerald-500 hover:text-white transition-colors"
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" /> Confirm payment
                          received
                        </button>
                      )}
                    <button
                      onClick={() => deleteBooking(b)}
                      data-testid={`delete-booking-${i}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-red-50 border border-red-100 px-3 py-1.5 text-xs font-bold text-red-500 hover:bg-red-500 hover:text-white transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Delete booking
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Print-only register — one page per camp day */}
      <div
        className="hidden print:block bg-white text-black px-6 py-4"
        data-testid="print-register"
      >
        <h1 className="text-2xl font-black">
          Action Packed Adventures — Christmas Camp Register
        </h1>
        <p className="text-sm mb-4">
          The Downs Preparatory School · 14–17 December 2026 · Printed{" "}
          {new Date().toLocaleDateString("en-GB", {
            day: "numeric",
            month: "long",
            year: "numeric",
          })}
        </p>
        {register.map((d, di) => (
          <div key={d.id} className={di < register.length - 1 ? "break-after-page" : ""}>
            <h2 className="mt-4 mb-2 text-lg font-black border-b-2 border-black">
              {d.label} — {d.entries.length}{" "}
              {d.entries.length === 1 ? "child" : "children"}
            </h2>
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr>
                  {[
                    "Child",
                    "Year",
                    "Drop-off",
                    "Collection",
                    "Medical",
                    "Emergency contact",
                    "Signed in",
                    "Signed out",
                  ].map((h) => (
                    <th
                      key={h}
                      className="border border-slate-400 px-2 py-1.5 text-left font-bold"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {d.entries.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="border border-slate-400 px-2 py-3 text-center text-slate-500"
                    >
                      Nobody booked
                    </td>
                  </tr>
                ) : (
                  d.entries.map((e, ei) => (
                    <tr key={ei}>
                      <td className="border border-slate-400 px-2 py-2 font-bold">
                        {e.name}
                        <span className="block font-normal text-slate-500">
                          {e.parent_name}
                        </span>
                      </td>
                      <td className="border border-slate-400 px-2 py-2">
                        {(e.school_year || "").replace("Year ", "Y")}
                      </td>
                      <td className="border border-slate-400 px-2 py-2">
                        {e.early_dropoff ? "8.00–8.30am" : "8.30–9.00am"}
                      </td>
                      <td className="border border-slate-400 px-2 py-2">
                        {e.late_collection ? "5.00–5.30pm" : "4.30–5.00pm"}
                      </td>
                      <td className="border border-slate-400 px-2 py-2">
                        {e.medical_info &&
                        e.medical_info.trim().toLowerCase() !== "none"
                          ? e.medical_info
                          : "—"}
                      </td>
                      <td className="border border-slate-400 px-2 py-2">
                        {[e.emergency_contact_name, e.emergency_contact_phone]
                          .filter(Boolean)
                          .join(" · ") || "—"}
                      </td>
                      <td className="border border-slate-400 px-2 py-2 w-16" />
                      <td className="border border-slate-400 px-2 py-2 w-16" />
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}

const StatCard = ({ label, value }) => (
  <div className="rounded-2xl bg-white border-2 border-slate-900/5 p-5 shadow-sm">
    <p className="text-sm font-bold text-slate-400 uppercase tracking-wide">
      {label}
    </p>
    <p className="mt-1 font-display font-black text-3xl text-slate-900">{value}</p>
  </div>
);
